import { Avatar, Empty, Input, Modal, Popover, Tooltip, Spin, Skeleton, message as antMessage } from "antd";
import {
    UserPlus,
    Pin,
    Headphones,
    Mail,
    Clock,
    Link,
    ArrowLeft,
    Info,
    Paperclip,
    ImageIcon,
    Megaphone,
    MailWarning,
    ExternalLink,
    Search,
    RefreshCw,
    Clapperboard,
    Cake,
} from "lucide-react";
import useAuthStore from "../../store/authStore";
import MessageBubble from "./MessageBubble";
import { useRef, useState, useEffect, useCallback } from "react";
import EmojiPicker from "emoji-picker-react";
import { fetchCareStatuses, mapCareStatusToLabel } from "./chatLabels"
import LabelBar from "./LabelBar";
import MediaModal from "../../components/MediaModal";
import { getChannelMessages, sendChannelMessage, sendInstagramMessage, ChannelMessageItem, AdInfo, getMessageSenderName, cleanStaffName } from "../../api/ChannelMessage";
import { getChannelComments, replyChannelComment, ChannelCommentItem } from "../../api/ChannelComment";
import { isRealtimeEnabled, subscribeChannelMessages, COL_CUSTOMER_ID } from "../../services/realtime";
import { REALTIME_FALLBACK_POLL } from "env";
import { updateChannelCustomer, restoreUnread, updateChannelCustomerAvatar, getChannelCustomerDetail } from "../../api/ChannelCustomer";
import { mixGetV1 } from "../../services/mixin";
import { uploadPhotoLibrary } from "../../api/PhotoLibrary";
import { useFbFinder } from "../../hooks/useFbFinder";
import {
    buildCommentPermalink,
    buildInboxUrl,
    buildPeopleSearchUrl,
    isFacebookChannel,
} from "../../utils/facebook";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import dayjs from "dayjs";

/**
 * Thẻ cho attachment "story_mention" — khách nhắc page trong tin của họ.
 * Payload chỉ có url media trên CDN của Meta, không kèm tiêu đề/ảnh thu nhỏ, và link
 * có chữ ký nên sẽ hết hạn: phát được thì hiện video, hỏng thì rơi về link mở tab mới.
 */
function StoryMentionCard({ url, label, openText, expiredText }: {
    url: string; label: string; openText: string; expiredText: string;
}) {
    const [failed, setFailed] = useState(false);
    const [ready, setReady] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Link hết hạn thì trình duyệt treo ở trạng thái đang tải chứ KHÔNG bắn sự kiện error,
    // để vậy người dùng nhìn mãi khung đen. Quá 8 giây chưa có metadata thì coi như hỏng.
    useEffect(() => {
        if (ready || failed) return;
        const timer = setTimeout(() => {
            if ((videoRef.current?.readyState ?? 0) === 0) setFailed(true);
        }, 8000);
        return () => clearTimeout(timer);
    }, [ready, failed]);

    return (
        <div className="flex justify-start mb-2">
            <div className="ml-10 max-w-[340px] w-full rounded-xl border border-gray-200 dark:border-[#333] bg-white dark:bg-[#262626] overflow-hidden shadow-sm">
                <div className="px-3 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-[#333] flex items-center gap-1">
                    <Clapperboard size={12} className="text-[#3e91ab]" />
                    {label}
                </div>

                {!failed ? (
                    <video
                        ref={videoRef}
                        src={url}
                        controls
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={() => setReady(true)}
                        onError={() => setFailed(true)}
                        className="w-full max-h-[320px] bg-black"
                    />
                ) : (
                    <div className="px-3 py-2 text-[12px] text-gray-500 dark:text-gray-400">
                        {expiredText}
                    </div>
                )}

                <div className="px-3 py-2 border-t border-gray-100 dark:border-[#333]">
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-[11px] text-[#3e91ab] hover:underline"
                    >
                        <ExternalLink size={11} />
                        {openText}
                    </a>
                </div>
            </div>
        </div>
    );
}

export default function ChatWindow({ user, activeLabels, toggleLabel, goBack, openInfo, connectionExternalId, isGroup }: any) {
    const { t, i18n } = useTranslation();
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const isReadOnly = !isSuperAdmin && !hasPermission("chats.reply");
    const [openQuickMessage, setOpenQuickMessage] = useState(false);
    const [openEmoji, setOpenEmoji] = useState(false);
    const emojiRef = useRef<any>(null);
    const [open, setOpen] = useState(false);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [careLabels, setCareLabels] = useState<any[]>([]);
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    // localStorage "user" có 2 dạng: đăng nhập mới lưu thẳng bản ghi user (fullname/name, avatar, id),
    // phiên cũ lưu dạng { user_id, fullname, info: {...} } -> đọc được cả hai
    const myAvatar = storedUser.avatar || storedUser.avatar_url || storedUser.info?.avatar || '';
    // Tài khoản đang đăng nhập = người gửi tin: hiện ở ô soạn tin và gắn cho tin đang gửi
    const myUserId: number | null = Number(storedUser.user_id ?? storedUser.id ?? storedUser.info?.id) || null;
    const myName: string = cleanStaffName(
        storedUser.fullname || storedUser.name || storedUser.info?.fullname || storedUser.info?.name
    );
    const [isPinned, setIsPinned] = useState(false);
    const [pinning, setPinning] = useState(false);
    const [restoringUnread, setRestoringUnread] = useState(false);

    useEffect(() => {
        setIsPinned(!!user?.isPinned);
    }, [user?.id, user?.isPinned]);

    const handleTogglePin = useCallback(async () => {
        if (!user?.id || pinning) return;
        const next = !isPinned;
        setPinning(true);
        setIsPinned(next);
        const res = await updateChannelCustomer(user.id, { is_ping: next ? 1 : 0 });
        if (res.status) {
            antMessage.success(next ? t("roomchat.pin_success") : t("roomchat.unpin_success"));
        } else {
            setIsPinned(!next);
            antMessage.error(t("roomchat.pin_error"));
        }
        setPinning(false);
    }, [user?.id, isPinned, pinning, t]);

    // Khôi phục số tin nhắn chưa đọc (lỡ đánh dấu đã đọc)
    const handleRestoreUnread = useCallback(async () => {
        if (!user?.id || restoringUnread) return;
        setRestoringUnread(true);
        const res = await restoreUnread(user.id);
        if (res.status) {
            antMessage.success(t("roomchat.restore_unread_success") || "Đã khôi phục tin chưa đọc");
            // Báo danh sách hội thoại làm mới badge unread
            window.dispatchEvent(new Event("conversation-unread-restored"));
        } else {
            antMessage.error(res.msg || t("roomchat.restore_unread_error") || "Khôi phục thất bại");
        }
        setRestoringUnread(false);
    }, [user?.id, restoringUnread, t]);

    useEffect(() => {
        fetchCareStatuses().then((items) => {
            setCareLabels(items.map(mapCareStatusToLabel));
        });
    }, []);

    const formatTime = (isoString: string) => {
        if (!isoString) return "";
        try {
            const date = new Date(isoString);
            return date.toLocaleString(i18n.language === 'vi' ? 'vi-VN' : 'en-US', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return isoString;
        }
    };

    const [messages, setMessages] = useState<Array<{
        id: number | string;
        text: string;
        sender: string;
        time: string;
        rawTime: string;
        isMe: boolean;
        /** Nhân viên gửi (tin outbound từ CMS); null = không xác định */
        createdById?: number | null;
        /** Tên nhân viên đã gửi (creator.fullname / staff_name; tin đang gửi thì là tên mình) */
        createdByName?: string;
        images: any[];
        messageType?: string;
        payload?: any;
        adInfo?: AdInfo | null;
        adJson?: AdInfo | null;
        adAttachments?: AdInfo[];
        storyUrl?: string | null;
        audios?: string[];
        videos?: string[];
        pending?: boolean;
        failed?: boolean;
    }>>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const prevMessageCountRef = useRef(0);

    // Tài liệu API: bản ghi lịch sử cũ trả payload dưới dạng chuỗi JSON
    const parsePayload = (raw: any) => {
        if (!raw) return null;
        if (typeof raw !== "string") return raw;
        try { return JSON.parse(raw); } catch { return null; }
    };

    // payload.url phẳng dùng chung cho ảnh/video/file, chỉ nhận các loại là ảnh
    const FLAT_URL_IMAGE_TYPES = ["image", "photo", "sticker"];

    // Không lọc theo message_type nữa: cùng một dạng payload xuất hiện ở nhiều
    // message_type khác nhau tuỳ kênh (vd "image" và "attachment" đều dùng
    // payload.message.attachments[]), nên cứ thử lần lượt mọi dạng đã biết.
    const extractImages = (msg: ChannelMessageItem) => {
        const payload = parsePayload(msg.payload);
        if (!payload) return [];

        const images: { preview: string; type: string }[] = [];
        const push = (url?: string) => {
            if (url && !images.some((i) => i.preview === url)) {
                images.push({ preview: url, type: "image" });
            }
        };

        // Zalo/webhook (đúng cấu trúc tài liệu API mô tả cho message_type "image")
        const webhookAttachments = payload?.message?.attachments;
        if (Array.isArray(webhookAttachments)) {
            webhookAttachments
                .filter((att: any) => att?.type === "image")
                .forEach((att: any) => push(att?.payload?.url || att?.payload?.thumbnail));
        }

        // Facebook: payload.attachments.data[].image_data
        const fbAttachments = payload?.attachments?.data;
        if (Array.isArray(fbAttachments)) {
            fbAttachments
                .filter((att: any) => att?.mime_type?.startsWith("image/") || att?.image_data)
                .forEach((att: any) => push(att?.image_data?.url || att?.image_data?.preview_url));
        }

        // Extension: payload.url phẳng, loại nằm ở payload.type (photo/sticker/...)
        if (typeof payload?.url === "string") {
            const kind = payload?.type || msg.message_type;
            if (FLAT_URL_IMAGE_TYPES.includes(kind)) push(payload.url);
        }

        return images;
    };

    // payload.message.attachments[] hiện có 2 loại cần hiển thị riêng:
    //   - ig_post       : bài quảng cáo khách bấm vào để nhắn  -> thẻ "Đến từ quảng cáo"
    //                     (payload: url ảnh, title, ig_post_media_id)
    //   - story_mention : khách nhắc page trong tin của họ      -> thẻ story kèm video
    //                     (payload: chỉ có url tới media trên CDN)
    const AD_ATTACHMENT_TYPES = ["ig_post", "fb_post", "post", "template", "ad"];

    // Tin nhắn thoại: attachments[].type === "audio", payload.url trỏ file .ogg trên CDN.
    // Có kênh gửi phẳng payload.url kèm payload.type = "audio" nên nhận cả hai dạng.
    const extractAudios = (msg: ChannelMessageItem): string[] => {
        const payload = parsePayload(msg.payload);
        if (!payload) return [];

        const urls: string[] = [];
        const push = (url?: string) => {
            if (url && !urls.includes(url)) urls.push(url);
        };

        const attachments = payload?.message?.attachments;
        if (Array.isArray(attachments)) {
            attachments
                .filter((att: any) => String(att?.type || "").toLowerCase() === "audio")
                .forEach((att: any) => push(att?.payload?.url));
        }

        if (typeof payload?.url === "string") {
            const kind = String(payload?.type || msg.message_type || "").toLowerCase();
            if (kind === "audio" || kind === "voice") push(payload.url);
        }

        return urls;
    };

    // Video: attachments[].type === "video", payload.url trỏ file video trên CDN của Meta.
    // Link CDN có chữ ký nên sẽ hết hạn. Có kênh gửi phẳng payload.url kèm
    // payload.type = "video" nên nhận cả hai dạng.
    const extractVideos = (msg: ChannelMessageItem): string[] => {
        const payload = parsePayload(msg.payload);
        if (!payload) return [];

        const urls: string[] = [];
        const push = (url?: string) => {
            if (url && !urls.includes(url)) urls.push(url);
        };

        const attachments = payload?.message?.attachments;
        if (Array.isArray(attachments)) {
            attachments
                .filter((att: any) => String(att?.type || "").toLowerCase() === "video")
                .forEach((att: any) => push(att?.payload?.url));
        }

        // Facebook Graph: payload.attachments.data[].video_data
        const fbAttachments = payload?.attachments?.data;
        if (Array.isArray(fbAttachments)) {
            fbAttachments
                .filter((att: any) => att?.mime_type?.startsWith("video/") || att?.video_data)
                .forEach((att: any) => push(att?.video_data?.url || att?.file_url));
        }

        if (typeof payload?.url === "string") {
            const kind = String(payload?.type || msg.message_type || "").toLowerCase();
            if (kind === "video") push(payload.url);
        }

        return urls;
    };

    const extractStoryMention = (msg: ChannelMessageItem): string | null => {
        const payload = parsePayload(msg.payload);
        const attachments = payload?.message?.attachments;
        if (!Array.isArray(attachments)) return null;
        const found = attachments.find(
            (att: any) => String(att?.type || "").toLowerCase() === "story_mention" && att?.payload?.url
        );
        return found?.payload?.url || null;
    };

    // attachments là MẢNG nên lấy hết, không chỉ phần tử đầu — một tin có thể đính kèm
    // nhiều bài viết/quảng cáo.
    const extractAdsFromAttachments = (msg: ChannelMessageItem): AdInfo[] => {
        const payload = parsePayload(msg.payload);
        const attachments = payload?.message?.attachments;
        if (!Array.isArray(attachments)) return [];

        return attachments
            .filter((att: any) => {
                const type = String(att?.type || "").toLowerCase();
                return AD_ATTACHMENT_TYPES.includes(type) && (att?.payload?.title || att?.payload?.url);
            })
            .map((att: any) => {
                const p = att.payload || {};
                return {
                    ad_id: p.ig_post_media_id || p.post_id || undefined,
                    ad_image: typeof p.url === "string" ? p.url : undefined,
                    post_title: p.title || undefined,
                    post_url: p.permalink_url || p.link || undefined,
                } as AdInfo;
            });
    };

    const mapMessages = useCallback((items: ChannelMessageItem[]) => {
        return items.map((msg: ChannelMessageItem) => ({
            id: msg.id,
            text: msg.text || "",
            sender: msg.direction === "inbound" ? user.name : t("roomchat.you"),
            time: formatTime(msg.sent_at),
            rawTime: msg.sent_at,
            adInfo: msg.ad_info ?? null,
            adJson: msg.ad_json ?? null,
            adAttachments: extractAdsFromAttachments(msg),
            storyUrl: extractStoryMention(msg),
            audios: extractAudios(msg),
            videos: extractVideos(msg),
            isMe: msg.direction === "outbound",
            createdById: msg.created_by_id ?? null,
            createdByName: getMessageSenderName(msg),
            images: extractImages(msg),
            messageType: msg.message_type,
            payload: msg.payload,
            pending: false,
            failed: false,
        }));
    }, [user?.name]);

    // Khách loại "comment": load bình luận thay vì tin nhắn chat
    const isComment = user?.type === "comment";
    // external_comment_id của bình luận khách (inbound) mới nhất -> dùng làm parent khi Admin trả lời
    const replyParentRef = useRef<string | null>(null);

    // Khi mở cửa sổ chat: gọi API chi tiết để xác định loại kênh gửi tin.
    //  - connection.channel_type_code insta/instagram -> Instagram: dùng API sendInstagramMessage
    //  - còn lại (Facebook, Zalo OA...)        -> dùng chung API channel-message
    //    (Zalo trước đây đi qua zalo-send-message và chỉ gửi được văn bản)
    const [isInstagram, setIsInstagram] = useState(false);
    // Link trang cá nhân của khách trên kênh: ưu tiên dữ liệu từ danh sách,
    // nếu chưa có thì lấy thêm từ API chi tiết bên dưới.
    const [detailProfileLink, setDetailProfileLink] = useState<string | null>(null);
    const [channelTypeCode, setChannelTypeCode] = useState<string>("");
    // Ten kenh khach dang chat (dung khi xem theo nhom kenh de biet tra loi qua kenh nao)
    const [detailConnectionName, setDetailConnectionName] = useState<string>("");
    useEffect(() => {
        setIsInstagram(false);
        setDetailProfileLink(null);
        setChannelTypeCode("");
        setDetailConnectionName("");
        if (!user?.id) return;
        let cancelled = false;
        getChannelCustomerDetail(user.id).then((res) => {
            if (cancelled) return;
            const d = res.data as any;
            if (!res.status || !d) return;
            setDetailProfileLink(d.profile_link || null);
            const typeCode = String(d.connection?.channel_type_code || '').toLowerCase();
            setChannelTypeCode(typeCode);
            setDetailConnectionName(d.connection?.display_name || "");
            if (typeCode === 'insta' || typeCode === 'instagram') {
                setIsInstagram(true);
            }
        });
        return () => { cancelled = true; };
    }, [user?.id]);

    // Tên kênh của khách: ưu tiên dữ liệu danh sách (có sẵn ngay), thiếu thì lấy từ API chi tiết
    const replyChannelName = user?.connectionName || detailConnectionName || "";

    // Link trang cá nhân để mở tab mới khi bấm vào tên khách ở header.
    // Chuẩn hoá: bỏ khoảng trắng, tự thêm https:// nếu API chỉ trả về domain.
    const profileLink = (() => {
        const raw = String(user?.profileLink || user?.profile_link || detailProfileLink || "").trim();
        if (!raw) return "";
        return /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`;
    })();

    // --- Tìm link Facebook thật qua extension "Khothi FB Finder" -------------
    // API không có quyền đổi PSID -> UID thật, nên phần này dựa vào phiên đăng nhập
    // Facebook của chính nhân viên: CMS chỉ đưa họ tới đúng bình luận/hội thoại,
    // extension bắt UID trên trang cá nhân họ mở và ghi ngược về CMS.
    const isFacebook = isFacebookChannel(channelTypeCode);
    const { installed: finderInstalled, findingId, startFind } = useFbFinder((u) => {
        if (u.customerId !== user?.id) return;
        if (u.profileLink) setDetailProfileLink(u.profileLink);
        if (!u.avatarUploaded) return;

        // Extension vừa upload ảnh mới -> lấy lại URL ảnh từ API và phát sự kiện chung
        // để cả header lẫn danh sách hội thoại đổi ảnh ngay, khỏi bắt nhân viên F5.
        getChannelCustomerDetail(u.customerId).then((res) => {
            const next = res.status && res.data ? (res.data as any) : null;
            const url = next ? next.avatar || next.avatar_url || "" : "";
            if (!url) return;
            setAvatar(url);
            window.dispatchEvent(
                new CustomEvent("channel-customer-avatar-updated", {
                    detail: { id: u.customerId, avatar: url },
                })
            );
        });
    }, (r) => {
        // Kết thúc một lượt dò. Thất bại thì extension đã tự đưa tab Facebook lên trước
        // mặt nhân viên, ở đây chỉ nói rõ vì sao.
        if (r.ok) {
            antMessage.success(t("roomchat.fb_find_done"));
            return;
        }
        antMessage.warning(
            r.reason === "NEED_PICK"
                ? t("roomchat.fb_find_need_pick")
                : r.reason === "TIMEOUT"
                    ? t("roomchat.fb_find_timeout")
                    : r.reason === "EXT_STALE"
                        ? t("roomchat.fb_find_stale")
                        : t("roomchat.fb_find_failed")
        );
    });

    const fbFindTarget = (() => {
        if (!isFacebook) return "";
        if (isComment) {
            const firstInbound = messages.find((m) => !m.isMe && m.payload);
            const permalink = firstInbound ? buildCommentPermalink(firstInbound.payload) : "";
            if (permalink) return permalink;
        }
        // Khách nhắn tin: vào hộp thư của page rồi để extension dò theo tên + tin cuối.
        return buildInboxUrl(connectionExternalId) || buildPeopleSearchUrl(user?.name);
    })();

    const mapComments = useCallback((items: ChannelCommentItem[]) => {
        const lastInbound = [...items].reverse().find((c) => c.direction === "inbound");
        replyParentRef.current = lastInbound?.external_comment_id || null;
        return items.map((c: ChannelCommentItem) => ({
            id: c.id,
            text: c.text || "",
            sender: c.direction === "inbound" ? user.name : t("roomchat.you"),
            time: formatTime(c.sent_at),
            rawTime: c.sent_at,
            adInfo: null,
            adJson: null,
            isMe: c.direction === "outbound",
            createdById: c.created_by_id ?? null,
            createdByName: getMessageSenderName(c),
            images: [] as any[],
            messageType: "text",
            payload: c.payload,
            pending: false,
            failed: false,
        }));
    }, [user?.name]);

    const fetchMessages = useCallback(async (isPolling = false) => {
        if (!user?.id) return;
        if (!isPolling) setLoadingMessages(true);
        // Khách loại comment -> lấy bình luận; còn lại -> tin nhắn chat
        const fetched = isComment
            ? await getChannelComments({ customer_id: user.id, sort: ["sent_at,asc"] }).then(
                r => (r.status && r.data ? mapComments(r.data.items) : null))
            : await getChannelMessages({ customer_id: user.id }).then(
                r => (r.status && r.data ? mapMessages(r.data.items) : null));
        if (fetched) {
            if (isPolling) {
                // Merge: keep pending/failed optimistic messages, replace confirmed ones
                setMessages(prev => {
                    const optimistic = prev.filter(m => m.pending || m.failed);
                    return [...fetched, ...optimistic];
                });
            } else {
                setMessages(fetched);
            }
        }
        if (!isPolling) setLoadingMessages(false);
    }, [user?.id, isComment, mapMessages, mapComments]);

    // Initial fetch
    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    // Cập nhật tin nhắn mới:
    // - Có Supabase Realtime: nghe thay đổi channel_messages của khách đang mở
    //   -> refetch. Kèm polling chậm 20s làm dự phòng khi websocket rớt.
    // - Chưa cấu hình Realtime: fallback polling 3s như cũ.
    useEffect(() => {
        if (!user?.id) return;

        if (isRealtimeEnabled()) {
            const unsub = subscribeChannelMessages({
                channelName: `room-chat-messages:${user.id}`,
                filter: `${COL_CUSTOMER_ID}=eq.${user.id}`,
                onEvent: () => fetchMessages(true),
            });
            const interval = REALTIME_FALLBACK_POLL
                ? setInterval(() => fetchMessages(true), 20000)
                : null;
            return () => {
                unsub();
                if (interval) clearInterval(interval);
            };
        }

        const interval = setInterval(() => fetchMessages(true), 3000);
        return () => clearInterval(interval);
    }, [user?.id, fetchMessages]);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        prevMessageCountRef.current = messages.length;
    }, [messages.length]);

    // Có tin nhắn ảnh mới -> báo cho danh sách "Hình ảnh khách gửi" nạp lại ảnh mới
    const prevImageMsgCountRef = useRef(0);
    useEffect(() => {
        const imageMsgCount = messages.reduce(
            (n, m) => n + ((m.images?.length || 0) > 0 ? 1 : 0),
            0
        );
        if (prevImageMsgCountRef.current !== 0 && imageMsgCount > prevImageMsgCountRef.current) {
            window.dispatchEvent(
                new CustomEvent("customer-photos-updated", { detail: user?.id })
            );
        }
        prevImageMsgCountRef.current = imageMsgCount;
    }, [messages, user?.id]);

    const [message, setMessage] = useState("");

    const employees = [
        "PhanCao Huân",
        "Trần Văn Tuấn",
        "Nguyễn thị thùy linh"
    ];

    const quickMessages = []; // placeholder
    const [quickMessageList, setQuickMessageList] = useState<{ id: number; title: string }[]>([]);
    const [loadingQuickMessages, setLoadingQuickMessages] = useState(false);

    const fetchQuickMessages = useCallback(async (search?: string) => {
        setLoadingQuickMessages(true);
        try {
            const res = await mixGetV1('/api/v1/be/quick-messages', {
                is_active: 1,
                search: search || undefined,
                sort: 'position,asc',
            });
            if (res.status && res.data?.items) {
                setQuickMessageList(res.data.items);
            }
        } catch (e) {
            console.error('Failed to fetch quick messages:', e);
        }
        setLoadingQuickMessages(false);
    }, []);

    // Nạp sẵn mẫu tin nhắn nhanh để gõ "/" là dùng được ngay
    useEffect(() => {
        fetchQuickMessages();
    }, [fetchQuickMessages]);

    const [openAssign, setOpenAssign] = useState(false);
    const [assigned, setAssigned] = useState<string | null>(null);
    const [files, setFiles] = useState<any[]>([]);

    const handleSelect = (name: string) => {
        setAssigned(name);
        setOpenAssign(false);
    };

    const sendMessage = async () => {
        if (!message.trim() && files.length === 0) return;
        if (!user?.id) return;

        // Khách loại comment: trả lời bình luận (chỉ văn bản) qua API channel-comment
        if (isComment) {
            const replyText = message.trim();
            if (!replyText) return;
            const parentId = replyParentRef.current;
            if (!parentId) {
                antMessage.error(t("roomchat.comment_no_parent") || "Chưa có bình luận để trả lời");
                return;
            }
            const tId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
            const nowIso = new Date().toISOString();
            setMessages(prev => [...prev, {
                id: tId, text: replyText, images: [], sender: t("roomchat.you"),
                time: formatTime(nowIso), rawTime: nowIso, isMe: true, pending: true, failed: false,
                createdById: myUserId, createdByName: myName,
            }]);
            setMessage("");
            const r = await replyChannelComment({ customer_id: user.id, parent_comment_id: parentId, text: replyText });
            if (r.status) {
                setMessages(prev => prev.filter(m => m.id !== tId));
                fetchMessages(true);
            } else {
                setMessages(prev => prev.map(m => m.id === tId ? { ...m, pending: false, failed: true } : m));
                antMessage.error(r.msg || t("roomchat.comment_reply_error") || "Trả lời bình luận thất bại");
            }
            return;
        }

        const text = message.trim();
        const currentFiles = files;
        const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const now = new Date().toISOString();

        // Optimistic: show message instantly, clear input immediately
        setMessages(prev => [...prev, {
            id: tempId,
            text,
            images: currentFiles,
            sender: t("roomchat.you"),
            time: formatTime(now),
            rawTime: now, // <-- sửa chỗ này
            isMe: true,
            createdById: myUserId,
            createdByName: myName,
            pending: true,
            failed: false,
        }]);
        setMessage("");
        setFiles([]);

        try {
            // 1) Lấy URL ảnh: file cục bộ -> upload thư viện ảnh; ảnh từ thư viện đã có URL
            const imageUrls: string[] = [];
            for (const f of currentFiles) {
                if (f?.file instanceof File) {
                    const up = await uploadPhotoLibrary({ image: f.file, name: f.file.name || `chat_${Date.now()}` });
                    const url = up?.data?.image;
                    if (!up.status || !url) throw new Error("upload failed");
                    imageUrls.push(url);
                } else if (typeof f?.preview === "string" && /^https?:\/\//.test(f.preview)) {
                    imageUrls.push(f.preview);
                }
            }

            // 2) Gửi tin nhắn văn bản (nếu có)
            if (text) {
                if (isInstagram) {
                    // Kênh Instagram: gửi qua API instagram-message
                    const r = await sendInstagramMessage({ customer_id: user.id, text, message_type: "text" });
                    if (!r.status) throw new Error(r.msg || "send instagram failed");
                } else {
                    const r = await sendChannelMessage({ customer_id: user.id, text, message_type: "text" });
                    if (!r.status) throw new Error("send text failed");
                }
            }

            // 3) Gửi từng ảnh dưới dạng message_type "image" với payload.url
            for (const url of imageUrls) {
                const r = isInstagram
                    ? await sendInstagramMessage({ customer_id: user.id, message_type: "image", payload: { url } })
                    : await sendChannelMessage({ customer_id: user.id, message_type: "image", payload: { url } });
                if (!r.status) throw new Error("send image failed");
            }

            // Thành công: bỏ tin tạm, nạp lại từ server
            setMessages(prev => prev.filter(m => m.id !== tempId));
            fetchMessages(true);
        } catch (e) {
            setMessages(prev => prev.map(m =>
                m.id === tempId ? { ...m, pending: false, failed: true } : m
            ));
        }
    };

    const assignContent = (
        <div className="w-[260px]">
            <div className="text-[14px] font-semibold mb-3 text-000000e0">
                {t("roomchat.assign_conversation")}
            </div>

            <div className="max-h-[200px] overflow-y-auto">

                {employees.map((name, index) => (
                    <div
                        key={index}
                        onClick={() => handleSelect(name)}
                        className="
                        px-3 py-2
                        cursor-pointer
                        hover:bg-gray-100
                        dark:hover:bg-[#262626]
                        text-000000e0
                        "
                    >
                        {name}
                    </div>
                ))}

            </div>
        </div>
    );

    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: any) => {

        const file = e.target.files[0];
        if (!file) return;

        const preview = URL.createObjectURL(file);

        const newFile = {
            file,
            preview,
            type: file.type
        };

        setFiles(prev => [...prev, newFile]);
    };

    useEffect(() => {

        const handleClickOutside = (event: any) => {
            if (emojiRef.current && !emojiRef.current.contains(event.target)) {
                setOpenEmoji(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };

    }, []);

    const [search, setSearch] = useState("");

    const filtered = quickMessageList;

    const renderTimeDivider = (time: string) => {
        return (
            <div className="flex justify-center my-4">
                <span className="px-4 py-1 rounded-full bg-[#bcbcbc] text-white text-[12px]">
                    {time} Hôm nay
                </span>
            </div>
        );
    };

    const getDividerLabel = (time: string) => {
        const date = dayjs(time);

        if (date.isSame(dayjs(), "day")) {
            return `${date.format("HH:mm")} Hôm nay`;
        }

        if (date.isSame(dayjs().subtract(1, "day"), "day")) {
            return `${date.format("HH:mm")} Hôm qua`;
        }

        return date.format("HH:mm DD/MM/YYYY");
    };

    const shouldShowDivider = (
        currentTime: string,
        prevTime?: string
    ) => {
        if (!prevTime) return true;

        const current = dayjs(currentTime);
        const prev = dayjs(prevTime);

        // khác ngày
        if (!current.isSame(prev, "day")) {
            return true;
        }

        // cách nhau 12 tiếng
        return current.diff(prev, "hour") >= 12;
    };

    const [showQuickList, setShowQuickList] = useState(false);
    const [selectedQuickIndex, setSelectedQuickIndex] = useState(0);
    // Ref tới item đang chọn để cuộn theo khi di chuyển bằng phím lên/xuống
    const activeQuickItemRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (showQuickList) {
            activeQuickItemRef.current?.scrollIntoView({ block: "nearest" });
        }
    }, [selectedQuickIndex, showQuickList]);

    // Gõ /1 khi còn /10../19 thì chờ 500ms xem có gõ tiếp số nữa không rồi mới bung
    const QUICK_EXPAND_DELAY = 500;
    const quickExpandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Nội dung ô nhập mới nhất, để timer biết người dùng có sửa gì thêm không
    const messageRef = useRef(message);
    useEffect(() => { messageRef.current = message; }, [message]);

    const clearQuickExpandTimer = () => {
        if (quickExpandTimerRef.current) {
            clearTimeout(quickExpandTimerRef.current);
            quickExpandTimerRef.current = null;
        }
    };

    useEffect(() => clearQuickExpandTimer, []);
    const [avatar, setAvatar] = useState(user.avatar);
    // Đồng bộ avatar theo khách đang mở (tránh giữ avatar của khách cũ khi đổi hội thoại)
    useEffect(() => { setAvatar(user?.avatar || ""); }, [user?.id, user?.avatar]);
    const avatarInputRef = useRef<HTMLInputElement>(null);

    // Không dựng avatar từ UID: graph.facebook.com/{uid}/picture khi không có token chỉ
    // trả hình bóng người mặc định, hiển thị ra còn khó nhận ra khách hơn là chữ cái đầu.
    // Ảnh fbcdn đang lưu vẫn hết hạn sau 4-30 ngày; muốn vá triệt để phải proxy ảnh
    // phía server bằng page access token.
    const avatarSrc = avatar || undefined;

    const [uploadingAvatar, setUploadingAvatar] = useState(false);
    const handleChangeAvatar = async (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // cho phép chọn lại cùng file
        if (!file || !user?.id) return;

        const prev = avatar;
        const localPreview = URL.createObjectURL(file);
        setAvatar(localPreview); // hiển thị tạm ngay
        setUploadingAvatar(true);
        const res = await updateChannelCustomerAvatar(user.id, file);
        if (res.status) {
            // API cập nhật trả về đường dẫn avatar không đúng -> gọi API chi tiết lấy avatar chuẩn
            const detail = await getChannelCustomerDetail(user.id);
            const newUrl = (detail.status && detail.data)
                ? ((detail.data as any).avatar || (detail.data as any).avatar_url || localPreview)
                : localPreview;
            setAvatar(newUrl);
            antMessage.success(t("roomchat.update_success") || "Cập nhật ảnh đại diện thành công");
            // đồng bộ avatar ở danh sách hội thoại
            window.dispatchEvent(new CustomEvent("channel-customer-avatar-updated", { detail: { id: user.id, avatar: newUrl } }));
        } else {
            setAvatar(prev); // lỗi -> trả về ảnh cũ
            antMessage.error(res.msg || t("roomchat.update_failed") || "Cập nhật ảnh thất bại");
        }
        setUploadingAvatar(false);
    };

    return (
        <div className="flex flex-col h-full w-full">

            {/* HEADER */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-color-1f1f1f bg-white dark:bg-[#1f1f1f]">

                <div className="flex items-center gap-2">

                    {/* Back button (mobile) */}
                    <button
                        onClick={goBack}
                        className="md:hidden mr-1 text-gray-500 hover:text-[#3e91ab]"
                    >
                        <ArrowLeft size={20} />
                    </button>

                    <div
                        className="relative group cursor-pointer"
                        onClick={() => avatarInputRef.current?.click()}
                    >
                        <Avatar
                            src={avatarSrc}
                            size={40}
                            className="dark:bg-[#2a2a2a]"
                        >
                            {user?.name?.charAt(0) || "?"}
                        </Avatar>

                        <div
                            className={`
                                absolute inset-0
                                rounded-full
                                bg-black/40
                                transition
                                flex items-center
                                justify-center
                                ${uploadingAvatar ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
                            `}
                        >
                            {uploadingAvatar
                                ? <Spin size="small" />
                                : <ImageIcon size={16} className="text-white" />}
                        </div>
                    </div>

                    <input
                        ref={avatarInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleChangeAvatar}
                    />

                    {/* Tên khách + hàng icon thao tác nhanh ngay bên dưới */}
                    <div className="flex flex-col min-w-0">

                        {/* Không dùng class .text-000000e0 cho link vì nó set color !important,
                            sẽ chặn màu hover -> đặt màu chữ trực tiếp cho cả sáng/tối */}
                        {profileLink ? (
                            <Tooltip title={profileLink}>
                                <a
                                    href={profileLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-medium hidden md:flex items-center gap-1 no-underline hover:no-underline text-[#000000e0] dark:text-[#e5e5e5] hover:text-[#3e91ab] dark:hover:text-[#3e91ab] transition-colors"
                                >
                                    {user.name}
                                    <ExternalLink size={14} className="shrink-0 opacity-60" />
                                </a>
                            </Tooltip>
                        ) : (
                            <span className="font-medium text-000000e0 hidden md:block">
                                {user.name}
                            </span>
                        )}

                        {/* Cập nhật Giới tính & Ngày sinh: mở panel Thông tin khách hàng rồi bật hộp thoại */}
                        {!isReadOnly && (
                            <Tooltip title={t("roomchat.gender_birthday")}>
                                <button
                                    onClick={() => {
                                        openInfo?.(true);
                                        window.dispatchEvent(new CustomEvent("open-gender-birthday"));
                                    }}
                                    className="w-fit text-gray-400 hover:text-[#3e91ab] transition-colors"
                                >
                                    <Cake size={14} />
                                </button>
                            </Tooltip>
                        )}
                    </div>

                    {/* Khách Facebook: extension tự dò link + ảnh đại diện.
                        Vẫn giữ nút cho khách ĐÃ có link để chạy lại lấy ảnh mới - khách hay
                        đổi ảnh, và ảnh cũ lưu trong CMS thì không tự cập nhật theo.
                        Không có extension thì ẩn hẳn cho đỡ rối. */}
                    {isFacebook && finderInstalled && (
                        <Tooltip
                            title={
                                profileLink
                                    ? t("roomchat.fb_refresh_tooltip")
                                    : fbFindTarget
                                        ? t("roomchat.fb_find_tooltip")
                                        : t("roomchat.fb_find_tooltip_manual")
                            }
                        >
                            <button
                                disabled={findingId === user.id}
                                onClick={() =>
                                    startFind({
                                        customerId: user.id,
                                        customerName: user.name,
                                        lastMessage: user.lastMessage,
                                        targetUrl: fbFindTarget,
                                    })
                                }
                                className="hidden md:flex items-center gap-1 px-2 py-[2px] text-[12px] border rounded-md text-gray-500 hover:text-[#3e91ab] hover:border-[#3e91ab] transition-colors disabled:opacity-60 disabled:cursor-default disabled:hover:text-gray-500"
                            >
                                {findingId === user.id ? (
                                    <>
                                        <Spin size="small" />
                                        {t("roomchat.fb_finding")}
                                    </>
                                ) : (
                                    <>
                                        {profileLink ? <RefreshCw size={13} /> : <Search size={13} />}
                                        {profileLink ? t("roomchat.fb_refresh") : t("roomchat.fb_find")}
                                    </>
                                )}
                            </button>
                        </Tooltip>
                    )}

                </div>

                <div className="flex items-center gap-4 text-gray-500">

                    {/* Info button (tablet/mobile) */}
                    <button
                        onClick={openInfo}
                        className="xl:hidden hover:text-[#3e91ab]"
                    >
                        <Info onClick={() => openInfo(true)} size={18} />
                    </button>

                    {assigned && (
                        <div className="px-2 py-[2px] text-[12px] border rounded-md bg-gray-50 dark:bg-[#262626]">
                            {assigned}
                        </div>
                    )}

                    {!isReadOnly && (
                        <>
                            <Tooltip title={t("roomchat.restore_unread_tooltip")}>
                                <MailWarning
                                    size={18}
                                    onClick={handleRestoreUnread}
                                    className={`cursor-pointer transition-colors ${restoringUnread ? "opacity-50 pointer-events-none" : "text-gray-500 hover:text-[#3e91ab]"}`}
                                />
                            </Tooltip>

                            <Popover
                                content={assignContent}
                                trigger="click"
                                open={openAssign}
                                onOpenChange={setOpenAssign}
                                placement="bottomRight"
                            >
                                <Tooltip title={t("roomchat.assign_tooltip")}>
                                    <UserPlus size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                                </Tooltip>
                            </Popover>

                            {/* Tạm ẩn tính năng ghim thông báo (sẽ mở lại sau)
                            <Tooltip title={t("roomchat.pin_tooltip")}>
                                <Pin
                                    size={18}
                                    onClick={handleTogglePin}
                                    className={`
                                        cursor-pointer transition-colors
                                        ${pinning ? "opacity-50 pointer-events-none" : ""}
                                        ${isPinned ? "text-[#3e91ab]" : "text-gray-500 hover:text-[#3e91ab]"}
                                    `}
                                    fill={isPinned ? "#3e91ab" : "none"}
                                />
                            </Tooltip>
                            */}

                            {/* <Tooltip title={t("roomchat.support_tooltip")}>
                        <Headphones size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                    </Tooltip>

                    <Tooltip title={t("roomchat.email_tooltip")}>
                        <Mail size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                    </Tooltip>

                    <Tooltip title={t("roomchat.history_tooltip")}>
                        <Clock size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                    </Tooltip>

                    <Tooltip title={t("roomchat.copy_link_tooltip")}>
                        <Link size={18} className="cursor-pointer hover:text-[#3e91ab]" />
                    </Tooltip> */}
                        </>
                    )}

                </div>

            </div>

            {/* MESSAGE LIST */}
            <div className="flex-1 overflow-y-auto p-4 bg-[#f0f2f5] dark:bg-[#181818]">
                {loadingMessages ? (
                    <div className="flex flex-col gap-4">
                        {Array.from({ length: 6 }).map((_, idx) => {
                            const isMe = idx % 2 === 1;
                            return (
                                <div
                                    key={`msg-skeleton-${idx}`}
                                    className={`flex items-end gap-2 ${isMe ? "flex-row-reverse" : "flex-row"}`}
                                >
                                    <Skeleton.Avatar active size={36} shape="circle" />
                                    <Skeleton.Button
                                        active
                                        size="large"
                                        shape="round"
                                        style={{ width: idx % 3 === 0 ? 220 : 150, height: 40 }}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <>
                        {messages.map((msg, index) => {
                            const prevMsg = messages[index - 1];

                            const showDivider = shouldShowDivider(
                                msg.rawTime,
                                prevMsg?.rawTime
                            );

                            // Nhiều nhân viên cùng chat: hiện tên người gửi ở tin đầu mỗi chuỗi
                            // tin liên tiếp của cùng một người (giống Messenger)
                            const sameSenderAsPrev = !!prevMsg && prevMsg.isMe && !showDivider
                                && (prevMsg.createdByName || "") === (msg.createdByName || "");
                            const staffName = msg.isMe && msg.createdByName && !sameSenderAsPrev
                                ? msg.createdByName
                                : undefined;

                            return (
                                <div key={msg.id}>

                                    {showDivider && (
                                        <div className="flex justify-center my-4">
                                            <span
                                                className="
                                                    px-4
                                                    py-1
                                                    rounded-full
                                                    bg-[#bcbcbc]
                                                    text-white
                                                    text-[12px]
                                                    shadow-sm
                                                "
                                            >
                                                {getDividerLabel(msg.rawTime)}
                                            </span>
                                        </div>
                                    )}

                                    {msg.storyUrl && (
                                        <StoryMentionCard
                                            url={msg.storyUrl}
                                            label={t("roomchat.story_mention")}
                                            openText={t("roomchat.story_open")}
                                            expiredText={t("roomchat.story_expired")}
                                        />
                                    )}

                                    {(() => {
                                        // Gộp ad_json (chi tiết hơn) đè lên ad_info, bỏ khoá rỗng để
                                        // nguồn sau không xoá dữ liệu nguồn trước.
                                        const base: AdInfo = {};
                                        [msg.adInfo, msg.adJson].forEach((src) => {
                                            if (!src) return;
                                            Object.entries(src).forEach(([k, v]) => {
                                                if (v !== undefined && v !== null && v !== "") base[k] = v;
                                            });
                                        });

                                        // Một tin có thể kèm nhiều bài viết -> dựng danh sách thẻ,
                                        // bỏ trùng theo ad_id / nội dung bài.
                                        const hasContent = (a: AdInfo) => !!(a.ad_id || a.ad_name || a.creative_name || a.post_title);
                                        const ads: AdInfo[] = [];
                                        const seen = new Set<string>();
                                        const add = (a?: AdInfo | null) => {
                                            if (!a || !hasContent(a)) return;
                                            const key = String(a.ad_id || a.post_title || a.ad_name);
                                            if (seen.has(key)) return;
                                            seen.add(key);
                                            ads.push(a);
                                        };
                                        add(base);
                                        (msg.adAttachments || []).forEach(add);
                                        if (ads.length === 0) return null;

                                        return ads.map((ad, adIndex) => {
                                            const adTitle = ad.ad_name || ad.creative_name;
                                            return (
                                            <div key={adIndex} className="flex justify-start mb-2">
                                                <div className="ml-10 max-w-[340px] w-full rounded-xl border border-gray-200 dark:border-[#333] bg-white dark:bg-[#262626] overflow-hidden shadow-sm">
                                                    <div className="px-3 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-[#333] flex items-center gap-1">
                                                        <Megaphone size={12} className="text-[#3e91ab]" />
                                                        {t("roomchat.from_ad")}
                                                    </div>
                                                    <div className="flex gap-2 p-2">
                                                        <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex-shrink-0 flex items-center justify-center overflow-hidden relative">
                                                            <Megaphone size={18} className="text-gray-400 absolute" />
                                                            {ad.ad_image && (
                                                                <img
                                                                    src={ad.ad_image}
                                                                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                                                                    className="w-full h-full object-cover relative"
                                                                    alt=""
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            {adTitle && (
                                                                <div className="text-[13px] font-medium text-gray-800 dark:text-gray-100 line-clamp-2">
                                                                    {adTitle}
                                                                </div>
                                                            )}
                                                            {ad.post_title && (
                                                                <div className={`line-clamp-2 mt-0.5 ${adTitle
                                                                    ? "text-[12px] text-gray-600 dark:text-gray-300"
                                                                    : "text-[13px] font-medium text-gray-800 dark:text-gray-100"
                                                                    }`}>
                                                                    {ad.post_title}
                                                                </div>
                                                            )}
                                                            {ad.campaign_name && (
                                                                <div className="text-[10px] text-gray-400 line-clamp-1 mt-0.5">
                                                                    {t("roomchat.ad_campaign")}: {ad.campaign_name}
                                                                </div>
                                                            )}
                                                            {ad.ad_id && (
                                                                <div className="text-[10px] text-gray-400 mt-0.5 truncate">
                                                                    ID: {ad.ad_id}
                                                                </div>
                                                            )}
                                                            {ad.post_url && (
                                                                <a
                                                                    href={ad.post_url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="inline-flex items-center gap-1 text-[11px] text-[#3e91ab] hover:underline mt-1"
                                                                >
                                                                    <ExternalLink size={11} />
                                                                    {t("roomchat.ad_view_post")}
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            );
                                        });
                                    })()}

                                    <MessageBubble
                                        text={msg.text}
                                        sender={msg.sender}
                                        staffName={staffName}
                                        time={msg.time}
                                        isMe={msg.isMe}
                                        avatar={msg.isMe ? myAvatar : user.avatar}
                                        images={msg.images}
                                        audios={msg.audios}
                                        videos={msg.videos}
                                        pending={msg.pending}
                                        failed={msg.failed}
                                    />
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            {isReadOnly && (
                <div className="border-t border-color-1f1f1f px-4 py-2 bg-white dark:bg-[#1f1f1f]">
                    <LabelBar
                        labels={careLabels}
                        activeLabels={activeLabels ?? user.labels ?? []}
                        onToggle={() => { }}
                    />
                </div>
            )}

            {/* INPUT */}
            {!isReadOnly && (<div className="border-t border-color-1f1f1f p-3 bg-white dark:bg-[#1f1f1f]">
                {files.length > 0 && (
                    <div className="flex gap-2 mb-2 flex-wrap">

                        {files.map((item, index) => (

                            <div key={index} className="relative group">
                                {/* Nút xoá */}
                                <div
                                    onClick={() => {
                                        setFiles(prev => prev.filter((_, i) => i !== index));
                                    }}
                                    className="absolute top-[-6px] right-[-6px] w-[18px] h-[18px] rounded-full bg-black/70 text-white text-[12px] flex items-center justify-center cursor-pointer opacity-0 group-hover:opacity-100 transition"
                                >
                                    <X size={12} />
                                </div>
                                {item.type.startsWith("image") ? (

                                    <img
                                        src={item.preview}
                                        className="w-[70px] h-[70px] object-cover rounded"
                                        alt="preview"
                                    />

                                ) : (

                                    <div className="px-3 py-2 bg-gray-100 rounded text-sm">
                                        {item.file.name}
                                    </div>

                                )}

                            </div>

                        ))}

                    </div>
                )}

                <LabelBar
                    labels={careLabels}
                    activeLabels={activeLabels ?? user.labels ?? []}
                    onToggle={(key: string, id?: number) => toggleLabel(user.id, key, id)}
                />

                {/* Xem theo nhóm kênh: danh sách gộp nhiều kênh nên phải nói rõ
                    tin trả lời sẽ đi qua kênh nào của khách */}
                {isGroup && replyChannelName && (
                    <div className="mt-2 flex items-center gap-1.5 text-[12px] text-gray-500 dark:text-gray-400">
                        <span>{t("roomchat.reply_via_channel") || "Trả lời qua kênh"}:</span>
                        <span className="font-medium text-[#3e91ab] truncate max-w-[260px]" title={replyChannelName}>
                            {replyChannelName}
                        </span>
                        {channelTypeCode && (
                            <span className="text-[10px] uppercase px-1.5 py-[1px] rounded bg-gray-100 dark:bg-[#2a2a2a] text-gray-500">
                                {channelTypeCode}
                            </span>
                        )}
                    </div>
                )}

                <div className="relative mt-2 flex flex-col items-end gap-2 border border-gray-300 rounded-lg px-[18px] py-[16px] bg-[#fff] dark:bg-[#262626]
                focus-within:border-[#16a34a]
                focus-within:ring-1
                focus-within:ring-[#16a34a]">
                    {showQuickList && filtered.length > 0 && (
                        <div
                            className="
                                absolute
                                bottom-[60px]
                                left-0
                                right-0
                                max-h-[260px]
                                overflow-y-auto
                                bg-white
                                dark:bg-[#1f1f1f]
                                border
                                border-gray-200
                                dark:border-[#333]
                                rounded-lg
                                shadow-lg
                                z-50
                            "
                        >
                            {filtered.map((item, index) => (
                                <div
                                    key={item.id}
                                    ref={selectedQuickIndex === index ? activeQuickItemRef : null}
                                    onClick={() => {
                                        setMessage(item.title);
                                        setShowQuickList(false);
                                    }}
                                    className={`
                                        px-3
                                        py-2
                                        cursor-pointer
                                        border-l-4
                                        flex
                                        gap-1
                                        items-center
                                        ${selectedQuickIndex === index
                                            ? "bg-green-50 border-green-600 dark:bg-[#162626]"
                                            : "border-transparent hover:bg-gray-50 dark:hover:bg-[#262626]"
                                        }
                                    `}
                                >
                                    <div className={`font-semibold text-[13px] ${selectedQuickIndex === index
                                        ? "text-green-700 dark:text-green-300"
                                        : "text-green-700 dark:text-green-500"
                                        }`}>
                                        /{index + 1}
                                    </div>

                                    <div className={`text-[13px] line-clamp-2 ${selectedQuickIndex === index
                                        ? "text-gray-900 dark:text-white font-medium"
                                        : "text-gray-600 dark:text-gray-300"
                                        }`}>
                                        {item.title}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <textarea
                        value={message}
                        onChange={(e) => {
                            const value = e.target.value;

                            setMessage(value);

                            // Gõ thêm ký tự nào cũng huỷ lượt hẹn giờ đang chờ
                            clearQuickExpandTimer();

                            if (value.startsWith("/")) {

                                const match = value.match(/^\/(\d+)$/);

                                if (match) {
                                    const num = Number(match[1]);
                                    const idx = num - 1;
                                    const item = filtered[idx];

                                    if (item) {
                                        // Highlight sẵn item khớp
                                        setSelectedQuickIndex(idx);

                                        // Còn số dài hơn bắt đầu bằng số đang gõ không?
                                        // (vd đang gõ /1 nhưng còn /10 ... /19 hợp lệ)
                                        const hasLongerCandidate = num * 10 <= filtered.length;

                                        if (!hasLongerCandidate) {
                                            setMessage(item.title);
                                            setShowQuickList(false);
                                            return;
                                        }

                                        // Còn số dài hơn: chờ xem người dùng có gõ tiếp không
                                        setShowQuickList(true);

                                        quickExpandTimerRef.current = setTimeout(() => {
                                            quickExpandTimerRef.current = null;

                                            // Ô nhập đã đổi trong lúc chờ thì bỏ qua
                                            if (messageRef.current !== value) return;

                                            setMessage(item.title);
                                            setShowQuickList(false);
                                        }, QUICK_EXPAND_DELAY);

                                        return;
                                    }
                                }

                                setSelectedQuickIndex(0);
                                setShowQuickList(true);
                            } else {
                                setShowQuickList(false);
                            }
                        }}
                        onKeyDown={(e) => {

                            if (showQuickList) {

                                if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    clearQuickExpandTimer();

                                    setSelectedQuickIndex(prev =>
                                        Math.min(prev + 1, filtered.length - 1)
                                    );

                                    return;
                                }

                                if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    clearQuickExpandTimer();

                                    setSelectedQuickIndex(prev =>
                                        Math.max(prev - 1, 0)
                                    );

                                    return;
                                }

                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    clearQuickExpandTimer();

                                    const selected = filtered[selectedQuickIndex];

                                    if (selected) {
                                        setMessage(selected.title);
                                        setShowQuickList(false);
                                    }

                                    return;
                                }

                                if (e.key === "Escape") {
                                    clearQuickExpandTimer();
                                    setShowQuickList(false);
                                    return;
                                }
                            }

                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                        className="w-full resize-y min-h-[24px] outline-none bg-transparent text-sm text-000000e0 overflow-y-auto"
                        rows={1}
                        placeholder={t("Trả lời... (Gõ / để chọn mẫu tin nhắn nhanh)")}
                    />

                    {openEmoji && (
                        <div
                            ref={emojiRef}
                            className="absolute bottom-[70px] right-[10px] z-50"
                        >
                            <EmojiPicker
                                onEmojiClick={(emojiData) => {
                                    setMessage(prev => prev + emojiData.emoji);
                                }}
                            />
                        </div>
                    )}

                    <div className="w-full flex items-center justify-end gap-4 text-gray-400 text-lg">

                        {/* Nhiều nhân viên dùng chung hộp chat: cho biết tin sẽ được ghi tên ai */}
                        {myName && (
                            <div className="mr-auto min-w-0 flex items-center gap-1.5 text-[12px] text-gray-500 dark:text-gray-400">
                                <Avatar
                                    size={20}
                                    src={myAvatar || undefined}
                                    className="flex-shrink-0 bg-[#16a34a] text-white text-[10px]"
                                >
                                    {myName.charAt(0).toUpperCase()}
                                </Avatar>
                                <span className="whitespace-nowrap">{t("roomchat.replying_as")}:</span>
                                <span className="font-medium text-gray-700 dark:text-gray-200 truncate" title={myName}>
                                    {myName}
                                </span>
                            </div>
                        )}

                        {!isComment && (
                            <Tooltip title={t("roomchat.add_image_tooltip")}>
                                <span onClick={() => setOpen(true)} className="cursor-pointer text-000000e0 hover:text-[#3e91ab]">
                                    <svg viewBox="64 64 896 896" focusable="false" data-icon="picture" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M928 160H96c-17.7 0-32 14.3-32 32v640c0 17.7 14.3 32 32 32h832c17.7 0 32-14.3 32-32V192c0-17.7-14.3-32-32-32zm-40 632H136v-39.9l138.5-164.3 150.1 178L658.1 489 888 761.6V792zm0-129.8L664.2 396.8c-3.2-3.8-9-3.8-12.2 0L424.6 666.4l-144-170.7c-3.2-3.8-9-3.8-12.2 0L136 652.7V232h752v430.2zM304 456a88 88 0 100-176 88 88 0 000 176zm0-116c15.5 0 28 12.5 28 28s-12.5 28-28 28-28-12.5-28-28 12.5-28 28-28z"></path></svg>
                                </span>
                            </Tooltip>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            className="hidden"
                            onChange={handleFileChange}
                        />

                        <Tooltip title={t("roomchat.emoji_tooltip")}>
                            <span onClick={() => setOpenEmoji(!openEmoji)} className="cursor-pointer text-000000e0 hover:text-[#3e91ab]">
                                <svg viewBox="64 64 896 896" focusable="false" data-icon="smile" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M288 421a48 48 0 1096 0 48 48 0 10-96 0zm352 0a48 48 0 1096 0 48 48 0 10-96 0zM512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm263 711c-34.2 34.2-74 61-118.3 79.8C611 874.2 562.3 884 512 884c-50.3 0-99-9.8-144.8-29.2A370.4 370.4 0 01248.9 775c-34.2-34.2-61-74-79.8-118.3C149.8 611 140 562.3 140 512s9.8-99 29.2-144.8A370.4 370.4 0 01249 248.9c34.2-34.2 74-61 118.3-79.8C413 149.8 461.7 140 512 140c50.3 0 99 9.8 144.8 29.2A370.4 370.4 0 01775.1 249c34.2 34.2 61 74 79.8 118.3C874.2 413 884 461.7 884 512s-9.8 99-29.2 144.8A368.89 368.89 0 01775 775zM664 533h-48.1c-4.2 0-7.8 3.2-8.1 7.4C604 589.9 562.5 629 512 629s-92.1-39.1-95.8-88.6c-.3-4.2-3.9-7.4-8.1-7.4H360a8 8 0 00-8 8.4c4.4 84.3 74.5 151.6 160 151.6s155.6-67.3 160-151.6a8 8 0 00-8-8.4z"></path></svg>
                            </span>
                        </Tooltip>

                        {/* <Tooltip title={t("roomchat.quick_message_tooltip")}>
                            <span
                                onClick={() => {
                                    setOpenQuickMessage(true);
                                    setSearch("");
                                    fetchQuickMessages();
                                }}
                                className="cursor-pointer text-000000e0 hover:text-[#3e91ab]"
                            >
                                <svg viewBox="64 64 896 896" focusable="false" data-icon="thunderbolt" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M848 359.3H627.7L825.8 109c4.1-5.3.4-13-6.3-13H436c-2.8 0-5.5 1.5-6.9 4L170 547.5c-3.1 5.3.7 12 6.9 12h174.4l-89.4 357.6c-1.9 7.8 7.5 13.3 13.3 7.7L853.5 373c5.2-4.9 1.7-13.7-5.5-13.7zM378.2 732.5l60.3-241H281.1l189.6-327.4h224.6L487 427.4h211L378.2 732.5z"></path></svg>
                            </span>
                        </Tooltip> */}
                        {!isComment && (
                            <Tooltip title="Đính kèm file">
                                <span
                                    onClick={handleUploadClick}
                                    className="cursor-pointer text-000000e0 hover:text-[#3e91ab]"
                                >
                                    <Paperclip size={18} />
                                </span>
                            </Tooltip>
                        )}

                        <Tooltip title={t("roomchat.send_tooltip")}>
                            <span
                                onClick={sendMessage}
                                className={`cursor-pointer text-000000e0 hover:text-[#3e91ab]`}
                            >
                                <svg viewBox="64 64 896 896" focusable="false" data-icon="send" width="1em" height="1em" fill="currentColor" aria-hidden="true"><defs><style></style></defs><path d="M931.4 498.9L94.9 79.5c-3.4-1.7-7.3-2.1-11-1.2a15.99 15.99 0 00-11.7 19.3l86.2 352.2c1.3 5.3 5.2 9.6 10.4 11.3l147.7 50.7-147.6 50.7c-5.2 1.8-9.1 6-10.3 11.3L72.2 926.5c-.9 3.7-.5 7.6 1.2 10.9 3.9 7.9 13.5 11.1 21.5 7.2l836.5-417c3.1-1.5 5.6-4.1 7.2-7.1 3.9-8 .7-17.6-7.2-21.6zM170.8 826.3l50.3-205.6 295.2-101.3c2.3-.8 4.2-2.6 5-5 1.4-4.2-.8-8.7-5-10.2L221.1 403 171 198.2l628 314.9-628.2 313.2z"></path></svg>
                            </span>
                        </Tooltip>

                    </div>

                </div>

                <Modal
                    open={openQuickMessage}
                    onCancel={() => setOpenQuickMessage(false)}
                    footer={null}
                    width={520}
                    title={t("roomchat.quick_message_title")}
                >
                    <Input
                        placeholder={t("roomchat.search_quick_message")}
                        className="mb-4"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            fetchQuickMessages(e.target.value);
                        }}
                    />

                    <Spin spinning={loadingQuickMessages}>
                        <div className="max-h-[320px] overflow-y-auto space-y-2">
                            {filtered.length === 0 ? (
                                <div className="h-[220px] flex items-center justify-center">
                                    <Empty description={t("roomchat.no_data")} />
                                </div>
                            ) : (
                                filtered.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => {
                                            setMessage(item.title);
                                            setOpenQuickMessage(false);
                                        }}
                                        className="border rounded-lg p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1f1f1f] transition"
                                    >
                                        <div className="text-gray-500 text-[13px] text-000000e0 line-clamp-2">
                                            {item.title}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </Spin>
                </Modal>

                <MediaModal
                    open={open} onClose={() => setOpen(false)}
                    onSelectImages={(imgs) => {
                        setFiles((prev) => [...prev, ...imgs]); // 🔥 add vào files
                    }}
                />

            </div>)}

        </div>
    );
}